import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import GetCookie from './GetCookie';
import PaginationControls from './PaginationControls';
import authNavigate from '../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
};

const FabricSelector = ({ selectedFabricId, selectedFabricName, onFabricSelect, disabled, fabricselectorapi, closeOnOutsideClick = false}) => {
  const [fabrics, setFabrics] = useState([]);
  const [total, setTotal] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const dropdownRef = useRef(null);

  useOutsideClick(dropdownRef, () => {
    if (closeOnOutsideClick) setIsDropdownOpen(false);
  });

  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch fabrics when dropdown opens or search/pagination changes
  useEffect(() => {
    if (isDropdownOpen) {
      fetchFabrics();
    }
  }, [isDropdownOpen, page, size, search]);

  const fetchFabrics = async () => {
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        fabricselectorapi,
        {
          include_documents: false, // We just need fabric list
          page,
          size,
          search: search || undefined
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'success') {
        setFabrics(data.data || []);
        setTotal(data.total || 0);
      }
      
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // Handle fabric selection
  const handleSelectFabric = (fabricId, fabricName) => {
    onFabricSelect(fabricId, fabricName);
    setIsDropdownOpen(false);
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      {/* Fabric selection button */}
      <button
        type="button"
        className="formField3 d-flex justify-content-between align-items-center w-100 text-start"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled= {disabled}
      >
        <span className= "ev-small-p-blue">
          {selectedFabricName || "Select Project"}
        </span>
        {!disabled && (
          <i className={`bi ${isDropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        )}
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="ev-message-card2 d-block ev-card-3 position-absolute z-1000 mt-1 w-100 p-2" >
          {/* Search and pagination header */}
          <div className="d-flex position-relative pe-2" >
            <div className="col-6 mb-2 position-relative">
              <button
                type="button"
                className="ev-btn-lightblue-fill-round search-btn-inside"
                style={{ left: 0 }}
                onClick={handleSearch}
              >
                <i className="bi bi-search"></i>
              </button>
              <input
                type="text"
                className="search-input ms-0"
                placeholder="Search projects"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            <div className="justify-content-between align-items-center col-4 mt-2 ev-fs-12 text-end">
              <div>
                <button 
                  type="button"
                  className="ev-btn-light-fill ev-btn-xsmall" 
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>

                <span className="ev-xsmall-p-dark ms-2">
                  Page {page}/{Math.ceil(total / size) || 1}
                </span>

                <button 
                  type="button"
                  className="ev-btn-light-fill ms-2 ev-btn-xsmall" 
                  disabled={page >= Math.ceil(total / size)}
                  onClick={() => setPage(page + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
            <div className="justify-content-between align-items-center col-2 mt-1 ms-2">
              <PaginationControls 
                  initialSize={size}
                  onSizeChange={(newSize) => {
                    setSize(newSize);
                    setPage(1);
                  }}
                />
            </div>
          </div>
          
          {/* Fabric list */}
          <div className="scrollable-element vh-15" >
            {isLoading && (
              <div className="text-center p-3 margin-top-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            
            {!isLoading && fabrics.length === 0 && (
              <p className="ev-small-p-blue text-center mt-3">No projects found</p>
            )}
            
            {!isLoading && fabrics.length > 0 && (
              <div>
                {fabrics.map(fabric => (
                  <div 
                    key={fabric.id} 
                    className={`mb-0 ev-padding-small ev-xsmall-p-blue cursor-pointer  mb-1 me-2 ev-bold-med ${selectedFabricId === fabric.id ? 'ev-card-1 ev-radius-small list-element-selected' : ''}`}
                    onClick={() => handleSelectFabric(fabric.id, fabric.name)}
                  >
                      <i className="bi bi-boxes"></i>&nbsp; {fabric.name} &nbsp;
                      {selectedFabricId === fabric.id && (
                        <i className="bi bi-check-circle-fill text-success"></i>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>
        
        </div>
      )}
    </div>
  );
};

export default FabricSelector;